import stakingApi from '@/services/staking';
import { getPoolListV3 } from '@/services/pool';
import { queryChainList } from '@/services/wallet';
import { BannerConfig, PoolItem } from '@/types/staking';
import { POOL_TAB } from '@/utils/constants';
import { create } from 'zustand';
import { Chain } from '@/types/wallet';

export enum ChainCodeEnum {
  ETH = 'ETH',
  ARBI = 'ARBI',
  CAVAX = 'CAVAX',
}

interface ITokenInfo {
  chains: Chain[];
  thirdChains: Chain[];
  chainCode: string;
  chainId: number;
  contractAddress: string;
  decimal: number;
  iconDayUrl: string;
  iconNightUrl: string;
  symbol: string;
}

export const ethAddress = 'eeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';

const formatPoolInfo = (
  result: { poolExtendDTO: { poolLabelDTO: object } }[]
) => {
  const tagList = Object.values(POOL_TAB) || [];
  const poolInfo: { [key: string]: PoolItem[] } | any = {};
  if (tagList?.length) {
    tagList.forEach((tag) => {
      const filterResult =
        result.filter(
          (item: { poolExtendDTO: { poolLabelDTO: { [x: string]: any } } }) => {
            return Boolean(item?.poolExtendDTO?.poolLabelDTO?.[tag]);
          }
        ) || [];
      poolInfo[tag] = filterResult;
    });
  }
  return poolInfo;
};

interface StoreState {
  homeConfig: BannerConfig | any; // 主页配置信息
  airdrop: any;
  configLoading: boolean;
  poolInfo: { [key: string]: PoolItem[] };
  originPoolList: PoolItem[];
  poolLoading: boolean;
  tokenInfo: ITokenInfo;
  chains: Chain[];
  thirdChains: Chain[];
  redeemNotAllow: boolean;
}

type Action = {
  getHomeConfig: () => void;
  getPoolInfo: () => void;
  updateTokenInfo: (
    chainCode: ChainCodeEnum,
    contractAddresses: string[]
  ) => void;
  getChains: (force?: boolean) => void;
  setAirdrop: (any) => void;
  setRedeemNotAllow: (data: boolean) => void;
};

export const useStakingStore = create<StoreState & Action>()((set, get) => ({
  homeConfig: {}, // 主页配置信息
  airdrop: {}, // 空投
  configLoading: true,
  poolInfo: {},
  originPoolList: [],
  poolLoading: true,
  tokenInfo: {} as ITokenInfo,
  chains: [],
  thirdChains: [],
  redeemNotAllow: false,

  getHomeConfig: async () => {
    try {
      const { success, result: homeConfig }: any =
        await stakingApi.getV3HomeConfig();
      if (success && homeConfig) {
        set(() => ({ homeConfig, configLoading: false }));
      }
    } catch (error) {
      set(() => ({ configLoading: false }));
    }
  },

  // 设置空投模块数据
  setAirdrop: (data) => {
    set(() => ({ airdrop: data }));
  },

  setRedeemNotAllow: (data: boolean) => {
    set((state) => ({ ...state, redeemNotAllow: data }));
  },

  getPoolInfo: async () => {
    const state = get();
    try {
      const { success, result }: any = await getPoolListV3();
      if (success && result) {
        set(() => ({
          poolInfo: formatPoolInfo(result),
          poolLoading: false,
          originPoolList: result,
        }));

        const contractAddressMap = new Map<ChainCodeEnum, Set<string>>();
        result.forEach(
          (pool: {
            chainCode: ChainCodeEnum;
            baseCoin: { contractAddress: string }[];
            multipleCoins: { contractAddress: string }[];
            singleCoins: { contractAddress: string }[];
          }) => {
            if (!contractAddressMap.has(pool.chainCode as ChainCodeEnum)) {
              contractAddressMap.set(
                pool.chainCode as ChainCodeEnum,
                new Set()
              );
            }
            pool.baseCoin.forEach((token: { contractAddress: string }) =>
              contractAddressMap
                .get(pool.chainCode as ChainCodeEnum)
                ?.add(token.contractAddress)
            );
            pool.multipleCoins?.forEach((token: { contractAddress: string }) =>
              contractAddressMap
                .get(pool.chainCode as ChainCodeEnum)
                ?.add(token.contractAddress)
            );
            pool.singleCoins?.forEach((token: { contractAddress: string }) =>
              contractAddressMap
                .get(pool.chainCode as ChainCodeEnum)
                ?.add(token.contractAddress)
            );
          }
        );
        contractAddressMap.forEach((contractAddresses, chainCode) => {
          state.updateTokenInfo(chainCode, [...contractAddresses, ethAddress]);
        });
      } else {
        set(() => ({ poolLoading: false }));
        console.error('Fail to get pool list');
      }
    } catch (error) {
      set(() => ({ poolLoading: false }));
    }
  },

  updateTokenInfo: async (
    chainCode: ChainCodeEnum,
    contractAddresses: string[]
  ) => {
    const state: any = get();

    const { result, success } = await stakingApi.getCoinsInfo(
      chainCode,
      contractAddresses
    );
    if (success && result) {
      Object.entries(result).forEach(([contractAddress, value]) => {
        if (!state.tokenInfo[chainCode]) {
          state.tokenInfo[chainCode] = {};
        }

        state.tokenInfo[chainCode][contractAddress] = value;
        set(() => ({ tokenInfo: state.tokenInfo }));
      });
    }
  },

  /**
   * 获取链列表
   * @param force 强制刷新state链列表
   */
  async getChains(force = false) {
    const state = get();

    if (state.chains.length && !force) return;
    const { success, result } = await queryChainList();
    if (success) {
      /** baseToken临时兼容 */
      set(() => ({
        chains: result.walletChains.CLOUD.itemList.map((v) => ({
          ...v,
          baseToken: v.baseTokenName,
        })),
        thirdChains: result.walletChains.MNEMONIC_PHRASE.itemList.map((v) => ({
          ...v,
          baseToken: v.baseTokenName,
        })),
      }));
    }
  },
}));
